import React from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const CLIENT_ROLES = ["client_admin", "client_user"];
// Client viewers see a firm-released request ("Awaiting Client") as "Received" — it's their turn to act.
export function useIsClient() {
  const auth = useAuth();
  const roles = auth?.session?.roles || [];
  return roles.some((r) => CLIENT_ROLES.includes(r));
}
export function statusLabel(status, isClient) {
  if (isClient) return status === "Awaiting Client" ? "Received" : status;
  // Firm/internal view: a client-submitted request reads as "Received from client".
  return status === "Submitted" ? "Received from client" : status;
}

const STATUS_STYLES = {
  "Received": "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
  "Awaiting Client": "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900",
  "Submitted": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
  "Complete": "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
};

export function StatusBadge({ status, className }) {
  const isClient = useIsClient();
  return (
    <span data-testid={`status-badge-${status?.toLowerCase().replace(/\s/g, "-")}`}
      className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        STATUS_STYLES[status] || "bg-slate-100 text-slate-700 border-slate-200", className)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {statusLabel(status, isClient)}
    </span>
  );
}

export const TICKET_LABEL = { received: "Received", in_progress: "In Progress", completed: "Completed", closed: "Closed" };
const TICKET_STYLE = {
  received: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
  closed: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};
export function TicketStatus({ status }) {
  return (
    <span data-testid={`ticket-status-${status}`}
      className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        TICKET_STYLE[status] || TICKET_STYLE.received)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {TICKET_LABEL[status] || "Received"}
    </span>
  );
}

export function RolePill({ role, className }) {
  const LABELS = { tenant_admin: "Firm Admin", tenant_user: "Firm User" };
  const label = LABELS[role] || role?.replace(/_/g, " ");
  return (
    <span className={cn("inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300", className)}>
      {label}
    </span>
  );
}

export function Empty({ icon: Icon, title, hint, action }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 dark:border-slate-700 py-16 text-center">
      {Icon && <Icon className="h-10 w-10 text-slate-300 dark:text-slate-600 mb-3" />}
      <p className="font-heading text-lg font-semibold text-slate-700 dark:text-slate-200">{title}</p>
      {hint && <p className="mt-1 text-sm text-slate-500 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "#2563EB", sub }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="overline text-slate-400">{label}</span>
        {Icon && <Icon className="h-4 w-4" style={{ color: accent }} />}
      </div>
      <div className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
